import { useEffect, useRef, useState } from "react";

export const labelClass = "text-xs font-medium text-muted-foreground";
export const fieldClass =
  "mt-1.5 w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm text-foreground outline-none transition placeholder:text-muted-foreground/70 focus:border-primary focus:ring-2 focus:ring-primary/25 disabled:opacity-60";
export const ctaClass =
  "brand-gradient w-full rounded-lg py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 flex items-center justify-center gap-2";

export const MOBILE_RE = /^[6-9][0-9]{9}$/;
export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function OtpInput({ onChange, error, length = 6, autoFocus = true, testId }) {
  const [digits, setDigits] = useState(() => Array(length).fill(""));
  const refs = useRef([]);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (!error) return;
    setDigits(Array(length).fill(""));
    refs.current[0]?.focus();
  }, [error, length]);

  function commit(next) {
    setDigits(next);
    onChange(next.join(""));
  }

  function setAt(i, raw) {
    const chars = raw.replace(/\D/g, "");
    if (chars.length > 1) {
      const next = [...digits];
      for (let k = 0; k < chars.length && i + k < length; k++) next[i + k] = chars[k];
      commit(next);
      refs.current[Math.min(i + chars.length, length - 1)]?.focus();
      return;
    }
    const next = [...digits];
    next[i] = chars.slice(-1);
    commit(next);
    if (chars && i < length - 1) refs.current[i + 1]?.focus();
  }

  return (
    <div className={`flex gap-2 ${error ? "animate-shake" : ""}`} data-testid={testId}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          value={d}
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          aria-label={`Digit ${i + 1}`}
          onChange={(e) => setAt(i, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus();
          }}
          className={`h-12 w-full rounded-lg border bg-surface-2 text-center text-lg font-semibold tabular-nums text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30 ${
            error ? "border-down" : "border-border"
          }`}
        />
      ))}
    </div>
  );
}

export function useCountdown(active, from = 60) {
  const [left, setLeft] = useState(0);
  const [round, setRound] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    setLeft(from);
    const id = setInterval(() => setLeft((l) => (l > 0 ? l - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [active, from, round]);
  return [left, () => setRound((r) => r + 1)];
}

export function ChannelToggle({ value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-1 rounded-lg border border-border bg-surface-2 p-1">
      {[
        ["mobile", "Mobile number"],
        ["email", "Email ID"],
      ].map(([c, text]) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          data-testid={`auth-channel-${c}`}
          className={`rounded-md py-2 text-sm font-medium transition ${
            value === c ? "brand-gradient text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

export function DevOtpHint({ code }) {
  if (!code) return null;
  return (
    <p
      className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning"
      data-testid="otp-dev-hint"
    >
      Dev mode: your code is <span className="font-mono font-semibold">{code}</span>
    </p>
  );
}

export function VerifiedBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-up/10 px-2 py-0.5 text-xs font-medium text-up page-fade">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
        <path d="M20 6 9 17l-5-5" />
      </svg>
      Verified
    </span>
  );
}

export function ErrorText({ msg, testId }) {
  if (!msg) return null;
  return (
    <p className="text-xs text-down" data-testid={testId}>
      {msg}
    </p>
  );
}
